import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaSave, FaShieldAlt } from 'react-icons/fa';

const ACTIONS = [
    { key: 'can_view', label: 'View' },
    { key: 'can_create', label: 'Create' },
    { key: 'can_edit', label: 'Edit' },
    { key: 'can_delete', label: 'Delete' },
];

const MenuAccess = ({ data }) => {
    const header = { headers: { Authorization: `Bearer ${data.token}` } };
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState('operator');
    const [matrix, setMatrix] = useState({});
    const [draft, setDraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadMatrix = async () => {
        setLoading(true);
        try {
            const res = await axios.get('api/menu-access/matrix', header);
            setRoles(res.data.roles || []);
            setMatrix(res.data.menus || {});
            const role = selectedRole || res.data.roles?.[0] || 'operator';
            setSelectedRole(role);
            setDraft(flattenMenus(res.data.menus?.[role] || []));
        } catch (e) {
            console.error(e);
            Swal.fire('Gagal', 'Tidak bisa memuat data akses menu', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMatrix();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!selectedRole || !matrix[selectedRole]) return;
        setDraft(flattenMenus(matrix[selectedRole]));
    }, [selectedRole, matrix]);

    const flattenMenus = (menus, depth = 0) => {
        const rows = [];
        (menus || []).forEach((m) => {
            rows.push({ ...m, depth });
            rows.push(...flattenMenus(m.children || [], depth + 1));
        });
        return rows;
    };

    const updateCell = (menuId, actionKey, value) => {
        setDraft((prev) =>
            prev.map((row) => {
                if (row.menu_id !== menuId) return row;
                const next = { ...row, [actionKey]: value };
                if (actionKey === 'can_view' && !value) {
                    next.can_create = false;
                    next.can_edit = false;
                    next.can_delete = false;
                }
                if (actionKey !== 'can_view' && value) {
                    next.can_view = true;
                }
                return next;
            })
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const items = draft.map((row) => ({
                menu_id: row.menu_id,
                can_view: !!row.can_view,
                can_create: !!row.can_create,
                can_edit: !!row.can_edit,
                can_delete: !!row.can_delete,
            }));
            const res = await axios.put(
                `api/menu-access/role/${selectedRole}`,
                { items },
                header
            );
            setMatrix(res.data.menus || matrix);
            if (typeof data?.refreshAuth === 'function') {
                data.refreshAuth();
            } else {
                window.dispatchEvent(new Event('auth:refresh'));
            }
            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Akses menu berhasil disimpan.',
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (e) {
            console.error(e);
            Swal.fire('Gagal', 'Gagal menyimpan akses menu', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-3.5">
                <div className="mb-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-left">
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                            Kelola akses halaman & aksi
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Atur View / Create / Edit / Delete per role untuk setiap menu.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="tag-accent">
                            <FaShieldAlt className="h-3 w-3" />
                            Role Permission
                        </span>
                        <span className="text-sm text-gray-700 dark:text-gray-200">
                            Pilih Level :
                        </span>
                        <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        >
                            {roles.map((r) => (
                                <option key={r} value={r}>
                                    {r === 'admin' ? 'Admin' : r === 'operator' ? 'Operator' : r}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            disabled={saving || loading}
                            onClick={handleSave}
                            className="btn-primary !min-h-0 !px-3 !py-1.5 !text-sm"
                        >
                            <FaSave />
                            {saving ? 'Menyimpan...' : `Simpan`}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-sm text-gray-500">Memuat...</div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/40">
                                <tr>
                                    <th className="px-3 py-2 text-left font-bold text-gray-600 dark:text-gray-300">
                                        Menu
                                    </th>
                                    {ACTIONS.map((a) => (
                                        <th
                                            key={a.key}
                                            className="w-20 px-2 py-2 text-center font-bold text-gray-600 dark:text-gray-300"
                                        >
                                            {a.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {draft.map((row, idx) => (
                                    <tr
                                        key={row.menu_id}
                                        className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0
                                            ? 'bg-white dark:bg-gray-800'
                                            : 'bg-gray-50/60 dark:bg-gray-900/20'
                                            }`}
                                    >
                                        <td className="px-3 py-2 text-left">
                                            <div className="flex min-w-0 items-baseline gap-2">
                                                <span
                                                    className={`font-semibold text-gray-800 dark:text-gray-100 ${row.depth ? 'text-[13px]' : ''
                                                        }`}
                                                    style={{ paddingLeft: row.depth ? `${row.depth * 14}px` : 0 }}
                                                >
                                                    {row.depth ? '↳ ' : ''}
                                                    {row.name}
                                                </span>
                                                <span className="truncate text-[11px] text-gray-400 dark:text-gray-500">
                                                    {row.path || 'Group'}
                                                </span>
                                            </div>
                                        </td>
                                        {ACTIONS.map((a) => (
                                            <td key={a.key} className="w-20 px-2 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={!!row[a.key]}
                                                    onChange={(e) =>
                                                        updateCell(row.menu_id, a.key, e.target.checked)
                                                    }
                                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default MenuAccess;
